import jsPDF from "jspdf";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { BRAND } from "@/config/brand";
import logoImage from "@/assets/regard-de-nature-logo.png";
import notoSansRegularUrl from "@/assets/fonts/NotoSans-Regular.ttf";
import notoSansBoldUrl from "@/assets/fonts/NotoSans-Bold.ttf";
import { IridologyFormData, IrisPhoto, IridologyRecommendation } from "@/types/iridology";
import { IrisCalibration, IrisZone } from "@/components/iridology/IrisInteractiveMap";
import { IridologyObservationsData } from "@/components/iridology/IridologyObservationsForm";

interface EyeData {
  imageData: string | null;
  calibration: IrisCalibration | null;
}

interface ZoneObservation {
  id?: string;
  zone_id: string;
  zone_label: string;
  zone_ring: string;
  eye_side: string;
  observation_notes: string | null;
  severity_level: string | null;
  is_highlighted?: boolean;
}

export interface SelectedZoneWithDetails {
  zone: IrisZone;
  eyeSide: "D" | "G";
  observation?: string;
  severityLevel?: string;
}

export interface FullSessionPdfData {
  patientName: string;
  sessionDate: string;
  formData: IridologyFormData;
  photos: IrisPhoto[];
  recommendations?: IridologyRecommendation[];
  rightEye?: EyeData;
  leftEye?: EyeData;
  zoneObservations?: ZoneObservation[];
  selectedZones?: SelectedZoneWithDetails[];
  signatureUrl?: string;
  observationsData?: IridologyObservationsData;
  practitionerInfo?: {
    practitioner_name?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
}

export const generateFullSessionPdf = async (data: FullSessionPdfData) => {
  const {
    patientName,
    sessionDate,
    formData,
    photos,
    recommendations = [],
    rightEye,
    leftEye,
    zoneObservations = [],
    selectedZones = [],
    observationsData,
    practitionerInfo,
  } = data;

  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // ---- Embed a real Unicode TTF font (fix accents/ligatures/spacing) ----
  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const loadFontBase64 = async (url: string) => {
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    return arrayBufferToBase64(buf);
  };

  const ensurePdfFonts = async () => {
    const anyDoc = doc as any;
    if (anyDoc.__notoSansLoaded) return;

    const [regularB64, boldB64] = await Promise.all([
      loadFontBase64(notoSansRegularUrl),
      loadFontBase64(notoSansBoldUrl),
    ]);

    doc.addFileToVFS("NotoSans-Regular.ttf", regularB64);
    doc.addFileToVFS("NotoSans-Bold.ttf", boldB64);

    doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
    doc.addFont("NotoSans-Regular.ttf", "NotoSans", "italic");
    doc.addFont("NotoSans-Bold.ttf", "NotoSans", "bold");
    doc.addFont("NotoSans-Bold.ttf", "NotoSans", "bolditalic");

    // Map all existing helvetica calls to NotoSans without rewriting the whole file
    const originalSetFont = doc.setFont.bind(doc);
    (doc as any).setFont = (fontName: any, fontStyle?: any) => {
      const mappedName = fontName === "helvetica" ? "NotoSans" : fontName;
      return originalSetFont(mappedName, fontStyle);
    };

    doc.setFont("NotoSans", "normal");
    anyDoc.__notoSansLoaded = true;
  };

  const resolveImageToDataUrl = async (src: string) => {
    if (!src) return null;
    if (src.startsWith("data:")) return src;

    const res = await fetch(src);
    const blob = await res.blob();

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  };

  const dataUrlFormat = (dataUrl: string) =>
    dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";

  const renderEyeWithSelectedZones = async (
    imageDataUrl: string,
    calibration: IrisCalibration | null | undefined,
    zonesForEye: SelectedZoneWithDetails[]
  ) => {
    if (!imageDataUrl || !calibration) return imageDataUrl;

    const img = new Image();
    img.src = imageDataUrl;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Image load failed"));
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return imageDataUrl;

    ctx.drawImage(img, 0, 0);

    const centerX = calibration.centerX;
    const centerY = calibration.centerY;
    const pupilRadius = calibration.pupilRadius;
    const irisRadius = calibration.irisRadius;
    const innerRingOuterRadius = pupilRadius + (irisRadius - pupilRadius) * 0.4;

    const drawSector = (zone: IrisZone) => {
      const startRad = ((zone.startAngle - 90) * Math.PI) / 180;
      const endRad = ((zone.endAngle - 90) * Math.PI) / 180;

      const isInner = zone.ring === "inner";
      const innerR = isInner ? pupilRadius : innerRingOuterRadius;
      const outerR = isInner ? innerRingOuterRadius : irisRadius;

      ctx.beginPath();
      ctx.arc(centerX, centerY, outerR, startRad, endRad);
      ctx.arc(centerX, centerY, innerR, endRad, startRad, true);
      ctx.closePath();

      const hex = zone.color || "#3B82F6";
      const r = parseInt(hex.slice(1, 3), 16) || 59;
      const g = parseInt(hex.slice(3, 5), 16) || 130;
      const b = parseInt(hex.slice(5, 7), 16) || 246;

      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.28)`;
      ctx.fill();
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.9)`;
      ctx.lineWidth = Math.max(2, Math.round(img.naturalWidth / 600));
      ctx.stroke();
    };

    zonesForEye.forEach((z) => drawSector(z.zone));

    // NO calibration circles drawn in PDF — clean photo output

    return canvas.toDataURL("image/jpeg", 0.92);
  };

  await ensurePdfFonts();

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const lineHeight = 5;
  let y = margin;
  let pageNum = 1;

  // Brand colors
  const colors = {
    primary: BRAND.colors.rgb.forestGreen,
    secondary: BRAND.colors.rgb.softAccent,
    accent: BRAND.colors.rgb.gold,
    background: BRAND.colors.rgb.lightBackground,
    text: BRAND.colors.rgb.text,
    textLight: BRAND.colors.rgb.textLight,
    white: BRAND.colors.rgb.white,
  };

  const severityColors: Record<string, { r: number; g: number; b: number }> = {
    normal: { r: 34, g: 139, b: 34 },
    attention: { r: 218, g: 165, b: 32 },
    priority: { r: 200, g: 80, b: 80 },
  };

  const priorityLabels: Record<string, string> = {
    normal: "Normal",
    attention: "A surveiller",
    priority: "Prioritaire",
  };

  // ==================== HELPERS ====================
  const footerHeight = 25;
  const safeBottomY = pageHeight - footerHeight;

  const checkPageBreak = (extraSpace: number = 20) => {
    if (y + extraSpace > safeBottomY) {
      doc.addPage();
      pageNum++;
      y = 18;
      addPageHeader();
    }
  };

  const addPageHeader = () => {
    doc.setFontSize(7);
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    doc.setFont("helvetica", "italic");
    doc.text("Bilan iridologique - " + patientName, pageWidth / 2, 10, { align: "center" });
    doc.setDrawColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
    doc.setLineWidth(0.3);
    doc.line(margin, 13, pageWidth - margin, 13);
  };

  const addSectionTitle = (title: string) => {
    checkPageBreak(20);
    y += 4;
    // Thin left accent bar + text
    doc.setFillColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.rect(margin, y - 4, 3, 12, "F");
    doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
    doc.rect(margin + 3, y - 4, contentWidth - 3, 12, "F");
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(title, margin + 8, y + 3);
    y += 14;
  };

  const addSubSection = (title: string) => {
    checkPageBreak(12);
    y += 2;
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(title, margin + 2, y);
    y += 6;
  };

  const addText = (text: string, indent: number = 0) => {
    const lines = doc.splitTextToSize(text, contentWidth - indent);
    checkPageBreak(lines.length * lineHeight + 2);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(lines, margin + indent, y);
    y += lines.length * lineHeight + 2;
  };

  const addLabelValue = (label: string, value: string | null | undefined, indent: number = 0) => {
    if (!value || !value.trim()) return;
    checkPageBreak(10);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(label + " :", margin + indent, y);
    const labelWidth = doc.getTextWidth(label + " : ");
    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    const lines = doc.splitTextToSize(value, contentWidth - indent - labelWidth - 5);
    doc.text(lines, margin + indent + labelWidth, y);
    y += Math.max(lines.length * lineHeight, lineHeight) + 2;
  };

  const addMultilineField = (label: string, value: string | null | undefined) => {
    if (!value || !value.trim()) return;
    const lines = doc.splitTextToSize(value, contentWidth - 14);
    const boxHeight = lines.length * lineHeight + 6;
    checkPageBreak(boxHeight + (label ? 8 : 2));
    if (label) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
      doc.text(label + " :", margin + 2, y);
      y += 5;
    }
    doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
    doc.roundedRect(margin + 2, y - 2, contentWidth - 4, boxHeight, 1.5, 1.5, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(lines, margin + 6, y + 2);
    y += boxHeight + 4;
  };

  const addBadges = (badges: string[], badgeColor?: { r: number; g: number; b: number }) => {
    if (badges.length === 0) return;
    checkPageBreak(14);
    let xPos = margin + 2;
    let currentLineY = y;
    const color = badgeColor || colors.primary;
    const badgeHeight = 6;
    doc.setFontSize(7);
    badges.forEach((badge) => {
      const badgeWidth = doc.getTextWidth(badge) + 8;
      if (xPos + badgeWidth > pageWidth - margin) {
        currentLineY += badgeHeight + 3;
        xPos = margin + 2;
        checkPageBreak(badgeHeight + 3);
      }
      // Outlined badge style instead of filled
      doc.setDrawColor(color.r, color.g, color.b);
      doc.setLineWidth(0.4);
      doc.setFillColor(color.r, color.g, color.b);
      doc.roundedRect(xPos, currentLineY - 3, badgeWidth, badgeHeight, 2, 2, "FD");
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(badge, xPos + 4, currentLineY + 1);
      xPos += badgeWidth + 4;
    });
    y = currentLineY + badgeHeight + 5;
  };

  const addBulletList = (items: string[], indent: number = 5) => {
    items.forEach((item) => {
      const lines = doc.splitTextToSize("- " + item, contentWidth - indent - 8);
      checkPageBreak(lines.length * lineHeight + 2);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
      doc.text(lines, margin + indent, y);
      y += lines.length * lineHeight + 1;
    });
    y += 2;
  };

  // ==================== HEADER ====================
  const headerHeight = 55;
  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  doc.rect(0, 0, pageWidth, headerHeight, "F");

  // Fine gold line at bottom of header
  doc.setDrawColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.setLineWidth(0.8);
  doc.line(0, headerHeight, pageWidth, headerHeight);

  // Logo centred
  const logoSize = 28;
  const logoY = 4;
  try {
    doc.addImage(logoImage, "PNG", pageWidth / 2 - logoSize / 2, logoY, logoSize, logoSize);
  } catch (error) {
    console.log("Logo non charge");
  }

  // Brand name
  doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text(BRAND.name.line1, pageWidth / 2, logoY + logoSize + 5, { align: "center" });

  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text(BRAND.name.line2, pageWidth / 2, logoY + logoSize + 11, { align: "center" });

  // Baseline
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
  doc.text(`${BRAND.baseline.parts.text1} & ${BRAND.baseline.parts.text2}`, pageWidth / 2, logoY + logoSize + 16, { align: "center" });

  y = headerHeight + 5;

  // ==================== TITLE ====================
  doc.setFontSize(14);
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFont("helvetica", "bold");
  doc.text("BILAN IRIDOLOGIQUE", pageWidth / 2, y, { align: "center" });
  y += 5;

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.text("Lecture du terrain et accompagnement personnalise", pageWidth / 2, y, { align: "center" });
  y += 4;

  // Thin separator
  doc.setDrawColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
  doc.setLineWidth(0.3);
  doc.line(margin + 30, y, pageWidth - margin - 30, y);
  y += 6;

  // ==================== PRACTITIONER INFO ====================
  if (practitionerInfo?.practitioner_name) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(`${practitionerInfo.practitioner_name} - Naturopathe`, pageWidth / 2, y, { align: "center" });
    y += 6;

    if (practitionerInfo.address) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
      const addrLines = doc.splitTextToSize(practitionerInfo.address, contentWidth - 20);
      doc.text(addrLines, pageWidth / 2, y, { align: "center" });
      y += addrLines.length * 4 + 2;
    }

    if (practitionerInfo.phone || practitionerInfo.email) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
      const contactParts: string[] = [];
      if (practitionerInfo.phone) contactParts.push(`Tel : ${practitionerInfo.phone}`);
      if (practitionerInfo.email) contactParts.push(`Email : ${practitionerInfo.email}`);
      doc.text(contactParts.join("   |   "), pageWidth / 2, y, { align: "center" });
      y += 5;
    }
    y += 4;
  }

  // ==================== PATIENT INFO ====================
  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  doc.roundedRect(margin, y, contentWidth, 16, 2, 2, "F");

  const consultType =
    formData.consultation_type === "iridology_only"
      ? "Iridologie seule"
      : "Iridologie + Naturopathie";
  const eyeLabel =
    formData.eye_observed === "right"
      ? "Oeil droit"
      : formData.eye_observed === "left"
      ? "Oeil gauche"
      : "Les deux yeux";

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text("Consultant :", margin + 4, y + 5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
  doc.text(patientName, margin + 24, y + 5);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text("Type :", pageWidth / 2, y + 5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
  doc.text(consultType, pageWidth / 2 + 12, y + 5);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text("Oeil observe :", margin + 4, y + 11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
  doc.text(eyeLabel, margin + 30, y + 11);

  // Date on right
  doc.setFont("helvetica", "bold");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text("Date :", pageWidth / 2, y + 11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
  doc.text(format(parseISO(sessionDate), "d MMMM yyyy", { locale: fr }), pageWidth / 2 + 12, y + 11);

  y += 22;

  // ==================== COMPARISON IMAGES SECTION ====================
  // Resolve eye images: prefer rightEye/leftEye data, fallback to photos array
  let rightImgSrc = rightEye?.imageData || null;
  let leftImgSrc = leftEye?.imageData || null;
  
  if (!rightImgSrc && photos.length > 0) {
    const rightPhoto = photos.find((p) => p.eye_side === "D" || p.eye_side === "right");
    if (rightPhoto) rightImgSrc = rightPhoto.photo_url;
  }
  if (!leftImgSrc && photos.length > 0) {
    const leftPhoto = photos.find((p) => p.eye_side === "G" || p.eye_side === "left");
    if (leftPhoto) leftImgSrc = leftPhoto.photo_url;
  }

  if (rightImgSrc || leftImgSrc) {
    addSectionTitle("Images de l'iris (Comparaison)");

    const imgWidth = 70;
    const imgHeight = 70;
    const spacing = 15;
    const startX = (pageWidth - imgWidth * 2 - spacing) / 2;

    checkPageBreak(imgHeight + 30);

    const rightZones = selectedZones.filter((z) => z.eyeSide === "D");
    const leftZones = selectedZones.filter((z) => z.eyeSide === "G");

    const rightImg = rightImgSrc && rightEye?.calibration
      ? await renderEyeWithSelectedZones(rightImgSrc, rightEye.calibration, rightZones)
      : rightImgSrc;
    const leftImg = leftImgSrc && leftEye?.calibration
      ? await renderEyeWithSelectedZones(leftImgSrc, leftEye.calibration, leftZones)
      : leftImgSrc;

    // Draw labels
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text("Oeil Droit", startX + imgWidth / 2, y, { align: "center" });
    doc.text("Oeil Gauche", startX + imgWidth + spacing + imgWidth / 2, y, { align: "center" });
    y += 6;

    const drawEyeBox = async (imgDataUrl: string | null | undefined, x: number) => {
      if (!imgDataUrl) {
        doc.setFillColor(240, 240, 240);
        doc.roundedRect(x, y, imgWidth, imgHeight, 3, 3, "F");
        doc.setFontSize(8);
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text("Non disponible", x + imgWidth / 2, y + imgHeight / 2, { align: "center" });
        return;
      }

      try {
        const resolved = await resolveImageToDataUrl(imgDataUrl);
        if (!resolved) throw new Error("No image");
        doc.addImage(resolved, dataUrlFormat(resolved) as any, x, y, imgWidth, imgHeight);
      } catch (error) {
        doc.setFillColor(240, 240, 240);
        doc.roundedRect(x, y, imgWidth, imgHeight, 3, 3, "F");
        doc.setFontSize(8);
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text("Image non disponible", x + imgWidth / 2, y + imgHeight / 2, { align: "center" });
      }
    };

    await drawEyeBox(rightImg, startX);
    await drawEyeBox(leftImg, startX + imgWidth + spacing);

    y += imgHeight + 10;
  }

  // Photos section removed (duplicates "Images de l'iris")

  // ==================== SELECTED ZONES SECTION ====================
  if (selectedZones.length > 0) {
    addSectionTitle("Zones selectionnees pour l'analyse");

    const rightZones = selectedZones.filter((z) => z.eyeSide === "D");
    const leftZones = selectedZones.filter((z) => z.eyeSide === "G");

    const drawZoneDetails = (zones: SelectedZoneWithDetails[], eyeLabel: string) => {
      if (zones.length === 0) return;

      addSubSection(eyeLabel);

      zones.forEach((entry, idx) => {
        // Calculate approximate height for this zone block
        const descLines = doc.splitTextToSize(entry.zone.description_iridologie || "", contentWidth - 18);
        const actionsCount = entry.zone.pistes_action?.length || 0;
        const alertsCount = entry.zone.signaux_alerte?.length || 0;
        const hasObs = !!entry.observation;
        const estimatedHeight = 20 + descLines.length * lineHeight + actionsCount * 8 + alertsCount * 8 + (hasObs ? 25 : 0);
        
        checkPageBreak(Math.min(estimatedHeight, 80));

        // Zone title with color bullet
        const zoneColor = entry.zone.color || "#3B82F6";
        doc.setFillColor(
          parseInt(zoneColor.slice(1, 3), 16) || 100,
          parseInt(zoneColor.slice(3, 5), 16) || 100,
          parseInt(zoneColor.slice(5, 7), 16) || 100
        );
        doc.circle(margin + 4, y, 3, "F");

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
        doc.text(entry.zone.label, margin + 10, y + 1);
        // Calculate label width BEFORE changing font
        const zoneLabelWidth = doc.getTextWidth(entry.zone.label);

        // Ring label - display on new line to avoid overlap
        const ringLabel = entry.zone.ring === "inner" ? "Couronne interne" : "Couronne externe";
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text(ringLabel, margin + 10, y + 6);

        // Severity badge on the right
        if (entry.severityLevel) {
          const sevColor = severityColors[entry.severityLevel] || severityColors.normal;
          const sevLabel = priorityLabels[entry.severityLevel] || "Normal";
          doc.setFillColor(sevColor.r, sevColor.g, sevColor.b);
          const badgeWidth = doc.getTextWidth(sevLabel) + 12;
          doc.roundedRect(pageWidth - margin - badgeWidth, y - 4, badgeWidth, 8, 2, 2, "F");
          doc.setFontSize(7);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(255, 255, 255);
          doc.text(sevLabel, pageWidth - margin - badgeWidth + 6, y + 1);
        }

        y += 16;

        // Description iridologique box
        if (entry.zone.description_iridologie) {
          const descHeight = descLines.length * lineHeight + 10;
          checkPageBreak(descHeight + 5);
          
          doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
          doc.roundedRect(margin + 5, y - 3, contentWidth - 10, descHeight, 2, 2, "F");

          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
          doc.text("Lecture iridologique :", margin + 8, y + 2);
          y += 8;

          doc.setFont("helvetica", "normal");
          doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
          doc.text(descLines, margin + 8, y);
          y += descLines.length * lineHeight + 6;
        }

        // Pistes d'accompagnement
        if (entry.zone.pistes_action && entry.zone.pistes_action.length > 0) {
          const actionsHeight = entry.zone.pistes_action.length * 7 + 10;
          checkPageBreak(actionsHeight);
          
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
          doc.text("Pistes d'accompagnement :", margin + 8, y);
          y += 7;

          entry.zone.pistes_action.forEach((action) => {
            const actionLines = doc.splitTextToSize("- " + action, contentWidth - 28);
            checkPageBreak(actionLines.length * lineHeight + 2);
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
            doc.text(actionLines, margin + 12, y);
            y += actionLines.length * lineHeight + 2;
          });
          y += 4;
        }

        // Signaux d'alerte box
        if (entry.zone.signaux_alerte && entry.zone.signaux_alerte.length > 0) {
          const alertHeight = entry.zone.signaux_alerte.length * 7 + 14;
          checkPageBreak(alertHeight);
          
          doc.setFillColor(255, 250, 240);
          doc.roundedRect(margin + 5, y - 2, contentWidth - 10, alertHeight, 2, 2, "F");

          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(200, 120, 50);
          doc.text("Points de vigilance :", margin + 8, y + 3);
          y += 9;

          entry.zone.signaux_alerte.forEach((signal) => {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(160, 100, 40);
            const signalLines = doc.splitTextToSize("- " + signal, contentWidth - 28);
            doc.text(signalLines, margin + 10, y);
            y += signalLines.length * lineHeight + 2;
          });
          y += 5;
        }

        // Personal observation box
        if (entry.observation) {
          const obsLines = doc.splitTextToSize(entry.observation, contentWidth - 28);
          const obsHeight = obsLines.length * lineHeight + 14;
          checkPageBreak(obsHeight);
          
          doc.setFillColor(240, 250, 245);
          doc.roundedRect(margin + 5, y - 2, contentWidth - 10, obsHeight, 2, 2, "F");

          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
          doc.text("Mes observations :", margin + 8, y + 3);
          y += 9;

          doc.setFont("helvetica", "normal");
          doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
          doc.text(obsLines, margin + 10, y);
          y += obsLines.length * lineHeight + 6;
        }

        y += 10;
      });
    };

    drawZoneDetails(rightZones, "Oeil Droit");
    drawZoneDetails(leftZones, "Oeil Gauche");
  }

  // ==================== ZONE OBSERVATIONS SECTION ====================
  if (zoneObservations.length > 0) {
    addSectionTitle("Observations enregistrees");

    const rightObs = zoneObservations.filter((o) => o.eye_side === "D");
    const leftObs = zoneObservations.filter((o) => o.eye_side === "G");

    const drawObsList = (obs: ZoneObservation[], label: string) => {
      if (obs.length === 0) return;
      addSubSection(label);

      obs.forEach((observation) => {
        const noteLines = observation.observation_notes 
          ? doc.splitTextToSize(observation.observation_notes, contentWidth - 18)
          : [];
        const blockHeight = 10 + noteLines.length * lineHeight;
        checkPageBreak(blockHeight);

        const sevColor = severityColors[observation.severity_level || "normal"] || severityColors.normal;
        doc.setFillColor(sevColor.r, sevColor.g, sevColor.b);
        doc.circle(margin + 4, y, 2.5, "F");

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
        doc.text(observation.zone_label, margin + 10, y + 1);
        // Calculate label width BEFORE changing font
        const obsLabelWidth = doc.getTextWidth(observation.zone_label);

        const ringLabel = observation.zone_ring === "inner" ? "(Int.)" : "(Ext.)";
        doc.setFontSize(7);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text(ringLabel, margin + 12 + obsLabelWidth, y + 1);

        y += 8;

        if (observation.observation_notes && noteLines.length > 0) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
          doc.text(noteLines, margin + 10, y);
          y += noteLines.length * lineHeight + 5;
        }
      });
      y += 4;
    };

    drawObsList(rightObs, "Oeil Droit");
    drawObsList(leftObs, "Oeil Gauche");
  }

  // ==================== INFORMATIONS COMPLEMENTAIRES ====================
  {
    const infoItems: string[] = [];
    if (formData.patient_gender === 'female') infoItems.push("Genre : Femme");
    if (formData.patient_gender === 'male') infoItems.push("Genre : Homme");
    if (formData.is_pregnant) infoItems.push("Enceinte : Oui");
    if (formData.is_breastfeeding) infoItems.push("Allaitement : Oui");
    if (infoItems.length > 0) {
      addSectionTitle("Informations du consultant");
      addBadges(infoItems);
    }
  }

  // ==================== OBSERVATIONS IRIDOLOGIQUES (COMPLET) ====================
  if (observationsData) {
    addSectionTitle("Observations iridologiques");
    
    // Premières impressions
    const impressions: string[] = [];
    if (observationsData.aspect_general) impressions.push(`Aspect : ${observationsData.aspect_general === 'harmonique' ? 'Harmonique' : 'Dysharmonique'}`);
    if (observationsData.couleur) impressions.push(`Couleur : ${observationsData.couleur === 'franche' ? 'Franche' : 'Heterogene'}`);
    if (observationsData.trame) impressions.push(`Trame : ${observationsData.trame === 'compacte' ? 'Compacte' : 'Clairsemee'}`);
    if (observationsData.pupilles) impressions.push(`Pupilles : ${observationsData.pupilles === 'rondes' ? 'Rondes' : 'Deformees'}`);
    if (impressions.length > 0) {
      addSubSection("Premieres impressions");
      addBadges(impressions);
    }

    if (observationsData.elements_remarquables) {
      addMultilineField("Elements remarquables", observationsData.elements_remarquables);
    }
    
    // Constitution
    const constitutionLabels: Record<string, string> = {
      lymphatique: 'Lymphatique fibrillaire',
      hematogene: 'Hematogene',
      mixte_hepatique: 'Mixte hepatique',
      mixte_pancreatique: 'Mixte pancreatique',
    };
    const sousConstitutionLabels: Record<string, string> = {
      hydrogenoide: 'Hydrogenoide',
      neurogene: 'Neurogene',
      glandulaire: 'Glandulaire',
    };
    if (observationsData.constitution || observationsData.sous_constitution) {
      addSubSection("Constitution");
      if (observationsData.constitution) {
        addLabelValue("Constitution principale", constitutionLabels[observationsData.constitution] || observationsData.constitution);
      }
      if (observationsData.sous_constitution) {
        addLabelValue("Sous-constitution", sousConstitutionLabels[observationsData.sous_constitution] || observationsData.sous_constitution);
      }
    }
    
    // Trame
    if (observationsData.trame_od || observationsData.trame_og) {
      addSubSection("Trame irienne");
      addLabelValue("Densite", `OD : ${observationsData.trame_od || '-'}/5  |  OG : ${observationsData.trame_og || '-'}/5`);
    }
    
    // Relief irien
    const reliefLabels: Record<string, string> = {
      canadienne: 'Canadienne', bedouine: 'Bedouine', igloo: 'Igloo', belle_etoile: 'Belle etoile',
    };
    if (observationsData.relief_od || observationsData.relief_og) {
      addSubSection("Relief irien");
      if (observationsData.relief_od) addLabelValue("OD", reliefLabels[observationsData.relief_od] || observationsData.relief_od);
      if (observationsData.relief_og) addLabelValue("OG", reliefLabels[observationsData.relief_og] || observationsData.relief_og);
    }

    // Cercle pupillaire & SNA
    if (observationsData.cercle_pupillaire_visible || observationsData.parasympathique || observationsData.orthosympathique) {
      addSubSection("Cercle pupillaire & SNA");
      if (observationsData.cercle_pupillaire_visible) addLabelValue("Cercle pupillaire", observationsData.cercle_pupillaire_visible);
      const snaLabels: Record<string, string> = { ok: 'Normal', hyper: 'Hyper', hypo: 'Hypo' };
      if (observationsData.parasympathique) addLabelValue("Parasympathique", snaLabels[observationsData.parasympathique] || observationsData.parasympathique);
      if (observationsData.orthosympathique) addLabelValue("Orthosympathique", snaLabels[observationsData.orthosympathique] || observationsData.orthosympathique);
    }

    // Pupils details
    const diametrLabels: Record<string, string> = { mydriase: 'Mydriase', ok: 'Normal', myosis: 'Myosis' };
    const ovalLabels: Record<string, string> = { horizontale: 'Horizontale', verticale: 'Verticale', oblique: 'Oblique' };
    const decentLabels: Record<string, string> = { superieur: 'Superieur', inferieur: 'Inferieur', nasal: 'Nasal', temporal: 'Temporal' };

    const hasPupilD = observationsData.pupille_d_diametre || observationsData.pupille_d_bords_grignotes || observationsData.pupille_d_bords_cabosses || observationsData.pupille_d_ovalisation || observationsData.pupille_d_decentrement || observationsData.pupille_d_meplats;
    const hasPupilG = observationsData.pupille_g_diametre || observationsData.pupille_g_bords_grignotes || observationsData.pupille_g_bords_cabosses || observationsData.pupille_g_ovalisation || observationsData.pupille_g_decentrement || observationsData.pupille_g_meplats;

    if (hasPupilD || hasPupilG) {
      addSubSection("Analyse pupillaire");
      
      if (hasPupilD) {
        const pupilDItems: string[] = [];
        if (observationsData.pupille_d_diametre) pupilDItems.push(`Diametre : ${diametrLabels[observationsData.pupille_d_diametre] || observationsData.pupille_d_diametre}`);
        if (observationsData.pupille_d_bords_grignotes) pupilDItems.push("Bords grignotes");
        if (observationsData.pupille_d_bords_cabosses) pupilDItems.push("Bords cabosses");
        if (observationsData.pupille_d_ovalisation) pupilDItems.push(`Ovalisation : ${ovalLabels[observationsData.pupille_d_ovalisation] || observationsData.pupille_d_ovalisation}${observationsData.pupille_d_ovalisation_secteurs ? ` (${observationsData.pupille_d_ovalisation_secteurs})` : ''}`);
        if (observationsData.pupille_d_decentrement) pupilDItems.push(`Decentrement : ${decentLabels[observationsData.pupille_d_decentrement] || observationsData.pupille_d_decentrement}${observationsData.pupille_d_decentrement_secteurs ? ` (${observationsData.pupille_d_decentrement_secteurs})` : ''}`);
        if (observationsData.pupille_d_meplats) pupilDItems.push(`Meplats : ${observationsData.pupille_d_meplats}`);
        if (observationsData.pupille_d_anisocorie) pupilDItems.push("Anisocorie");
        addLabelValue("Pupille droite", pupilDItems.join(" / "));
      }

      if (hasPupilG) {
        const pupilGItems: string[] = [];
        if (observationsData.pupille_g_diametre) pupilGItems.push(`Diametre : ${diametrLabels[observationsData.pupille_g_diametre] || observationsData.pupille_g_diametre}`);
        if (observationsData.pupille_g_bords_grignotes) pupilGItems.push("Bords grignotes");
        if (observationsData.pupille_g_bords_cabosses) pupilGItems.push("Bords cabosses");
        if (observationsData.pupille_g_ovalisation) pupilGItems.push(`Ovalisation : ${ovalLabels[observationsData.pupille_g_ovalisation] || observationsData.pupille_g_ovalisation}${observationsData.pupille_g_ovalisation_secteurs ? ` (${observationsData.pupille_g_ovalisation_secteurs})` : ''}`);
        if (observationsData.pupille_g_decentrement) pupilGItems.push(`Decentrement : ${decentLabels[observationsData.pupille_g_decentrement] || observationsData.pupille_g_decentrement}${observationsData.pupille_g_decentrement_secteurs ? ` (${observationsData.pupille_g_decentrement_secteurs})` : ''}`);
        if (observationsData.pupille_g_meplats) pupilGItems.push(`Meplats : ${observationsData.pupille_g_meplats}`);
        addLabelValue("Pupille gauche", pupilGItems.join(" / "));
      }
    }

    // Intoxination
    const intoxination: string[] = [];
    if (observationsData.acide_oxalique) intoxination.push("Acide oxalique");
    if (observationsData.acide_pyruvique) intoxination.push("Acide pyruvique");
    if (observationsData.acide_urique) intoxination.push("Acide urique");
    if (observationsData.anneau_electrolytique) intoxination.push("Anneau electrolytique");
    if (observationsData.anneau_lipidique) intoxination.push("Anneau lipidique");
    if (observationsData.anneau_gerontoxon) intoxination.push("Gerontoxon");
    if (intoxination.length > 0) {
      addSubSection("Intoxination & anneaux");
      addBadges(intoxination, { r: 200, g: 120, b: 50 });
    }

    // Taches
    const taches: string[] = [];
    if (observationsData.taches_psores) taches.push("Psores");
    if (observationsData.taches_cholesterol) taches.push("Cholesterol");
    if (observationsData.taches_cicatrisation) taches.push("Cicatrisation");
    if (observationsData.taches_stress) taches.push("Stress");
    if (observationsData.taches_hemorragique) taches.push("Hemorragique");
    if (taches.length > 0) {
      addSubSection("Taches");
      addBadges(taches, { r: 160, g: 100, b: 60 });
    }
    
    // Signes iriens
    const signes: string[] = [];
    if (observationsData.radii_solari) signes.push("Radii Solari");
    if (observationsData.arcs_cercle) signes.push("Arcs de cercle");
    if (observationsData.vacuoles) signes.push("Vacuoles");
    if (observationsData.lacunes) signes.push("Lacunes");
    if (observationsData.cryptes) signes.push("Cryptes");
    if (signes.length > 0) {
      addSubSection("Signes iriens");
      addBadges(signes);
    }

    // Observations oculaires
    const obsOculaires: string[] = [];
    if (observationsData.anisocorie) obsOculaires.push("Anisocorie");
    if (observationsData.heterochromie) obsOculaires.push("Heterochromie");
    if (observationsData.strabisme) obsOculaires.push("Strabisme");
    if (observationsData.debut_cataracte) obsOculaires.push("Debut de cataracte");
    if (obsOculaires.length > 0) {
      addSubSection("Observations oculaires");
      addBadges(obsOculaires, { r: 120, g: 120, b: 160 });
    }
    if (observationsData.autre_observation) {
      addMultilineField("Autres observations", observationsData.autre_observation);
    }

    // Defauts de vision & correction
    const vision: string[] = [];
    if (observationsData.myopie) vision.push("Myopie");
    if (observationsData.hypermetropie) vision.push("Hypermetropie");
    if (observationsData.astigmatisme) vision.push("Astigmatisme");
    if (observationsData.presbytie) vision.push("Presbytie");
    const corrections: string[] = [];
    if (observationsData.lunettes_vue) corrections.push(`Lunettes de vue${observationsData.lunettes_vue_quand ? ` (${observationsData.lunettes_vue_quand})` : ''}`);
    if (observationsData.lentilles) corrections.push("Lentilles");
    if (observationsData.lunettes_repos) corrections.push("Lunettes de repos");
    if (vision.length > 0 || corrections.length > 0 || observationsData.autre_defaut_vision) {
      addSubSection("Vision & correction");
      if (vision.length > 0) addBadges(vision, { r: 100, g: 140, b: 180 });
      if (corrections.length > 0) addBulletList(corrections);
      if (observationsData.autre_defaut_vision) addLabelValue("Autre", observationsData.autre_defaut_vision);
    }

    // Conclusions psycho-emotionnelles
    if (observationsData.conclusions_psy) {
      addMultilineField("Conclusions psycho-emotionnelles", observationsData.conclusions_psy);
    }
  }

  // ==================== TERRAIN & ANALYSIS ====================
  const terrains: string[] = [];
  if (formData.terrain_nervous) terrains.push("Nerveux");
  if (formData.terrain_digestive) terrains.push("Digestif");
  if (formData.terrain_circulatory) terrains.push("Circulatoire");
  if (formData.terrain_eliminatory) terrains.push("Eliminatoire");
  if (formData.terrain_mixed) terrains.push("Mixte");

  if (terrains.length > 0) {
    addSectionTitle("Terrain dominant");
    addBadges(terrains);
  }

  // ==================== VITALITY ====================
  if (formData.vitality_level) {
    addSectionTitle("Vitalite globale");

    const vitalityLabels: Record<string, string> = {
      good: "Bonne",
      average: "Moyenne",
      needs_support: "A soutenir",
    };
    const vitalityColors: Record<string, { r: number; g: number; b: number }> = {
      good: { r: 34, g: 139, b: 34 },
      average: { r: 218, g: 165, b: 32 },
      needs_support: { r: 255, g: 140, b: 0 },
    };

    const vitalityLabel = vitalityLabels[formData.vitality_level] || "Non renseigne";
    const vColor = vitalityColors[formData.vitality_level] || colors.primary;

    addBadges([vitalityLabel], vColor);

    if (formData.vitality_comments) {
      addMultilineField("Commentaires", formData.vitality_comments);
    }
  }

  // ==================== ADAPTATIONS ====================
  const adaptations: string[] = [];
  if (formData.adaptation_good) adaptations.push("Bonne");
  if (formData.adaptation_stress_sensitivity) adaptations.push("Sensibilite au stress");
  if (formData.adaptation_fatigue) adaptations.push("Fatigabilite possible");

  if (adaptations.length > 0) {
    addSectionTitle("Capacites d'adaptation");
    addBadges(adaptations);
  }

  // ==================== ZONES ====================
  if (formData.sensitivity_zones || formData.support_zones) {
    addSectionTitle("Zones observees");
    addMultilineField("Zones de sensibilite", formData.sensitivity_zones);
    addMultilineField("Zones a soutenir", formData.support_zones);
  }

  // ==================== OVERLOADS ====================
  const overloads: string[] = [];
  if (formData.overload_digestive) overloads.push("Digestive");
  if (formData.overload_nervous) overloads.push("Nerveuse");
  if (formData.overload_emotional) overloads.push("Emotionnelle");
  if (formData.overload_eliminatory) overloads.push("Eliminatoire");
  if (formData.overload_circulatory) overloads.push("Circulatoire");

  if (overloads.length > 0) {
    addSectionTitle("Signes de surcharge");
    addBadges(overloads, { r: 200, g: 120, b: 50 });
  }

  // ==================== SUPPORT AXES ====================
  const axes: string[] = [];
  if (formData.axis_hygiene) axes.push("Hygiene de vie");
  if (formData.axis_nutrition) axes.push("Ajustements alimentaires");
  if (formData.axis_plants) axes.push("Soutien par les plantes");
  if (formData.axis_stress) axes.push("Gestion du stress");
  if (formData.axis_seasonal) axes.push("Accompagnement saisonnier");

  if (axes.length > 0) {
    addSectionTitle("Axes d'accompagnement proposes");
    addBadges(axes);
  }

  // ==================== OBSERVATIONS ====================
  if (formData.additional_observations) {
    addSectionTitle("Observations complementaires");
    addMultilineField("", formData.additional_observations);
  }

  // ==================== RECOMMENDATIONS ====================
  if (recommendations.length > 0) {
    addSectionTitle("Recommandations personnalisees");

    recommendations.forEach((rec) => {
      const descLines = rec.description ? doc.splitTextToSize(rec.description, contentWidth - 12) : [];
      const blockHeight = 15 + descLines.length * lineHeight;
      checkPageBreak(blockHeight);

      // Category badge
      doc.setFillColor(colors.accent.r, colors.accent.g, colors.accent.b);
      const catWidth = doc.getTextWidth(rec.category) + 12;
      doc.roundedRect(margin + 2, y - 3, catWidth, 8, 2, 2, "F");
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(rec.category, margin + 8, y + 2);

      // Title
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
      doc.text(rec.title, margin + catWidth + 10, y + 1);
      y += 12;

      // Description
      if (rec.description && descLines.length > 0) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
        doc.text(descLines, margin + 6, y);
        y += descLines.length * lineHeight + 6;
      }
      
      y += 4;
    });
  }

  // ==================== ORIENTATION BOX ====================
  checkPageBreak(40);
  y += 5;
  
  doc.setFillColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.roundedRect(margin, y, contentWidth, 24, 3, 3, "F");

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("Orientation de l'accompagnement", margin + 6, y + 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const orientationText =
    "Cette analyse iridologique oriente vers un accompagnement naturopathique personnalise visant a soutenir l'equilibre global et la vitalite du consultant.";
  const orientationLines = doc.splitTextToSize(orientationText, contentWidth - 14);
  doc.text(orientationLines, margin + 6, y + 16);
  y += 32;

  // ==================== SIGNATURE SECTION ====================
  if (data.signatureUrl) {
    checkPageBreak(45);

    doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
    doc.roundedRect(margin, y, contentWidth, 40, 3, 3, "F");

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text("Signature du praticien", margin + 6, y + 8);

    try {
      const sigImg = new Image();
      sigImg.src = data.signatureUrl;
      await new Promise<void>((resolve) => {
        sigImg.onload = () => resolve();
        sigImg.onerror = () => resolve();
      });

      if (sigImg.complete && sigImg.naturalWidth > 0) {
        const sigMaxW = 50;
        const sigMaxH = 25;
        const sigRatio = sigImg.naturalWidth / sigImg.naturalHeight;
        let sigW = sigMaxW;
        let sigH = sigW / sigRatio;
        if (sigH > sigMaxH) {
          sigH = sigMaxH;
          sigW = sigH * sigRatio;
        }
        doc.addImage(data.signatureUrl, "PNG", margin + 6, y + 12, sigW, sigH);
      }
    } catch (e) {
      console.error("Error adding signature to PDF:", e);
    }

    y += 45;
  }

  // ==================== FOOTER ON ALL PAGES ====================
  const addFooter = (pageIndex: number) => {
    doc.setPage(pageIndex);
    const footerY = pageHeight - 18;

    // Thin separator line
    doc.setDrawColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
    doc.setLineWidth(0.3);
    doc.line(margin, footerY - 2, pageWidth - margin, footerY - 2);

    doc.setFontSize(5.5);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    const disclaimer =
      "L'iridologie est un outil d'observation du terrain et ne constitue pas un diagnostic medical. Cette approche ne remplace en aucun cas un suivi medical.";
    doc.text(disclaimer, pageWidth / 2, footerY + 2, { align: "center", maxWidth: contentWidth });

    doc.setFontSize(6);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(BRAND.name.full, margin, pageHeight - 4);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    doc.text("Document personnel et confidentiel", pageWidth - margin, pageHeight - 4, { align: "right" });
  };

  const totalPages = doc.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    addFooter(i);
  }

  // Save
  const fileName = "bilan_iridologie_" + patientName.replace(/\s+/g, "_") + "_" + format(parseISO(sessionDate), "yyyy-MM-dd") + ".pdf";
  doc.save(fileName);
};
